import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, CheckCircle2, AlertCircle, Save, Eye, EyeOff } from "lucide-react";
import { Brand } from "../components/Brand";
import { useAuth } from "../lib/auth";
import { useInicio } from "../lib/permisos";

interface SmtpForm {
  host: string;
  port: number;
  secure: boolean;
  usuario: string;
  password: string;
  tienePassword: boolean;
  fromNombre: string;
  fromEmail: string;
  activo: boolean;
}

export default function SmtpConfigPage() {
  const inicio = useInicio();
  const { token } = useAuth();
  const [form, setForm] = useState<SmtpForm>({
    host: "", port: 465, secure: true, usuario: "", password: "",
    tienePassword: false, fromNombre: "", fromEmail: "", activo: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    fetch("/api/v1/smtp-config", { headers })
      .then(r => r.json())
      .then(d => {
        setForm(f => ({ ...f, ...d, password: "" }));
        setTestEmail(d.fromEmail || d.usuario || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const set = <K extends keyof SmtpForm>(k: K, v: SmtpForm[K]) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    setSaving(true); setSaved(false);
    try {
      const body: any = {
        host: form.host, port: form.port, secure: form.secure,
        usuario: form.usuario, fromNombre: form.fromNombre,
        fromEmail: form.fromEmail, activo: form.activo,
      };
      if (form.password) body.password = form.password; // solo si cambió
      const res = await fetch("/api/v1/smtp-config", { method: "PUT", headers, body: JSON.stringify(body) });
      if (res.ok) {
        setSaved(true);
        setForm(f => ({ ...f, password: "", tienePassword: f.tienePassword || !!f.password }));
        setTimeout(() => setSaved(false), 2500);
      }
    } finally { setSaving(false); }
  };

  const probar = async () => {
    setTesting(true); setTestResult(null);
    try {
      const body: any = { destino: testEmail };
      if (form.password) body.password = form.password;
      const res = await fetch("/api/v1/smtp-config/test", { method: "POST", headers, body: JSON.stringify(body) });
      const d = await res.json();
      setTestResult({ ok: d.ok, msg: d.mensaje });
    } catch {
      setTestResult({ ok: false, msg: "Error de conexión" });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-400">Cargando...</div>;

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3">
          <Link to={inicio} className="flex items-center gap-2 text-ink-500 hover:text-ink-800">
            <ArrowLeft className="size-4" /><Brand size="sm" />
          </Link>
          <h1 className="font-semibold text-ink-800 flex items-center gap-2">
            <Mail className="size-4" /> Configuración de Correo (SMTP)
          </h1>
          <div />
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-6 py-8 space-y-5">
        <p className="text-sm text-ink-500">
          Configura el servidor de correo para el envío automático de confirmaciones, recordatorios y notificaciones a los clientes.
        </p>

        {/* Servidor */}
        <div className="card p-5 space-y-4">
          <h2 className="font-semibold text-ink-800">Servidor SMTP</h2>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-ink-600 mb-1">Host / Servidor</label>
              <input value={form.host} onChange={e => set("host", e.target.value)}
                className="input w-full" placeholder="mail.tudominio.com" />
            </div>
            <div>
              <label className="block text-xs text-ink-600 mb-1">Puerto</label>
              <input type="number" value={form.port} onChange={e => set("port", Number(e.target.value))}
                className="input w-full" placeholder="465" />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="radio" checked={form.secure} onChange={() => { set("secure", true); set("port", 465); }} />
              SSL (puerto 465)
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="radio" checked={!form.secure} onChange={() => { set("secure", false); set("port", 587); }} />
              TLS (puerto 587)
            </label>
          </div>
        </div>

        {/* Credenciales */}
        <div className="card p-5 space-y-4">
          <h2 className="font-semibold text-ink-800">Credenciales</h2>
          <div>
            <label className="block text-xs text-ink-600 mb-1">Usuario / Email de la cuenta</label>
            <input value={form.usuario} onChange={e => set("usuario", e.target.value)}
              className="input w-full" placeholder="crm@tudominio.com" />
          </div>
          <div>
            <label className="block text-xs text-ink-600 mb-1">Contraseña</label>
            <div className="relative">
              <input type={showPass ? "text" : "password"} value={form.password}
                onChange={e => set("password", e.target.value)}
                className="input w-full pr-10"
                placeholder={form.tienePassword ? "•••••••• (guardada — escribe para cambiar)" : "Contraseña SMTP"} />
              <button type="button" onClick={() => setShowPass(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
                {showPass ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            {form.tienePassword && !form.password && (
              <p className="text-xs text-emerald-600 mt-1">✓ Hay una contraseña guardada. Déjalo vacío para mantenerla.</p>
            )}
          </div>
        </div>

        {/* Remitente */}
        <div className="card p-5 space-y-4">
          <h2 className="font-semibold text-ink-800">Remitente (From)</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-600 mb-1">Nombre que verá el cliente</label>
              <input value={form.fromNombre} onChange={e => set("fromNombre", e.target.value)}
                className="input w-full" placeholder="CRM Redesip" />
            </div>
            <div>
              <label className="block text-xs text-ink-600 mb-1">Email remitente</label>
              <input value={form.fromEmail} onChange={e => set("fromEmail", e.target.value)}
                className="input w-full" placeholder="crm@tudominio.com" />
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <div onClick={() => set("activo", !form.activo)}
              className={`w-11 h-6 rounded-full transition-colors relative ${form.activo ? "bg-brand-600" : "bg-ink-300"}`}>
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.activo ? "translate-x-5" : "translate-x-0"}`} />
            </div>
            <span className="text-sm text-ink-700">{form.activo ? "Envío de correos activado" : "Envío desactivado"}</span>
          </label>
        </div>

        {/* Guardar */}
        <button onClick={guardar} disabled={saving}
          className="btn-primary w-full py-3 flex items-center justify-center gap-2 font-semibold">
          <Save className="size-4" />
          {saving ? "Guardando..." : saved ? "✓ Guardado" : "Guardar configuración"}
        </button>

        {/* Probar */}
        <div className="card p-5 space-y-3 border-2 border-dashed border-ink-200">
          <h2 className="font-semibold text-ink-800 flex items-center gap-2">
            <Send className="size-4" /> Probar configuración
          </h2>
          <p className="text-xs text-ink-500">Envía un email de prueba para verificar que todo funciona.</p>
          <div className="flex gap-2">
            <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
              className="input flex-1" placeholder="email@destino.com" />
            <button onClick={probar} disabled={testing}
              className="btn-secondary flex items-center gap-2">
              <Send className="size-4" /> {testing ? "Enviando..." : "Enviar prueba"}
            </button>
          </div>
          {testResult && (
            <div className={`flex items-center gap-2 text-sm rounded-lg p-3 ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {testResult.ok ? <CheckCircle2 className="size-4 shrink-0" /> : <AlertCircle className="size-4 shrink-0" />}
              {testResult.msg}
            </div>
          )}
          <p className="text-xs text-ink-400">
            💡 Si cambiaste la contraseña pero aún no guardas, la prueba usará la nueva. Si no, usa la guardada.
          </p>
        </div>
      </div>
    </div>
  );
}
